import React, { useEffect, useState, useRef } from 'react';
import { AdminMembershipPolicyDto, RefundPolicyDto } from 'services/data-contracts';
import styled from 'styled-components';

const INPUT_PADDING_VERTICAL = 8;
const INPUT_PADDING_HORIZONTAL = 12;
const INPUT_HEIGHT = 40;

const StyledInputField = styled(InputField)`
  .MuiOutlinedInput-input {
    padding: ${INPUT_PADDING_VERTICAL}px ${INPUT_PADDING_HORIZONTAL}px !important;
  }

  .MuiInputBase-input {
    padding: ${INPUT_PADDING_VERTICAL}px ${INPUT_PADDING_HORIZONTAL}px !important;
  }

  .MuiInputBase-root {
    height: ${INPUT_HEIGHT}px;
  }
`;

// 개별 회원 등급 정책 아이템 컴포넌트
const MembershipPolicyItem = ({
  membershipPolicy,
  onUpdate,
  isChanged = false,
}: {
  membershipPolicy: AdminMembershipPolicyDto;
  onUpdate: (id: number, field: string, value: number) => void;
  isChanged?: boolean;
}) => {
  const [pointRateValue, setPointRateValue] = useState(
    membershipPolicy.pointRate?.toString() || '',
  );
  const [stayCountValue, setStayCountValue] = useState(
    membershipPolicy.stayCount?.toString() || '',
  );
  const [stayNightCountValue, setStayNightCountValue] = useState(
    membershipPolicy.stayNightCount?.toString() || '',
  );

  const handlePointRateChange = (value: string) => {
    setPointRateValue(value);
  };

  const handleStayCountChange = (value: string) => {
    setStayCountValue(value);
  };

  const handleStayNightCountChange = (value: string) => {
    setStayNightCountValue(value);
  };

  const handlePointRateBlur = () => {
    const numValue = Number(pointRateValue);
    if (!isNaN(numValue)) {
      onUpdate(membershipPolicy.id as number, 'pointRate', numValue);
    }
  };

  const handleStayCountBlur = () => {
    const numValue = Number(stayCountValue);
    if (!isNaN(numValue)) {
      onUpdate(membershipPolicy.id as number, 'stayCount', numValue);
    }
  };

  const handleStayNightCountBlur = () => {
    const numValue = Number(stayNightCountValue);
    if (!isNaN(numValue)) {
      onUpdate(membershipPolicy.id as number, 'stayNightCount', numValue);
    }
  };

  useEffect(() => {
    setPointRateValue(membershipPolicy.pointRate?.toString() || '');
    setStayCountValue(membershipPolicy.stayCount?.toString() || '');
    setStayNightCountValue(membershipPolicy.stayNightCount?.toString() || '');
  }, [membershipPolicy.pointRate, membershipPolicy.stayCount, membershipPolicy.stayNightCount]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: 20,
        alignItems: 'center',
        padding: '10px',
        borderRadius: '8px',
        backgroundColor: isChanged ? '#fff3cd' : 'transparent',
        border: isChanged ? '1px solid #ffeaa7' : '1px solid transparent',
        marginBottom: '10px',
      }}
    >
      <div style={{ minWidth: 150 }}>
        <span style={{ fontWeight: 600, fontSize: 20, color: 'var(--point-color)' }}>
          {membershipPolicy.level}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Row gap={10} justifyContent="flex-start">
          <StyledInputField
            onChange={handlePointRateChange}
            onBlur={handlePointRateBlur}
            type="number"
            style={{ width: 265 }}
            value={pointRateValue}
          />
          <div>적립율(%)</div>
        </Row>
        <Row gap={10} justifyContent="flex-start">
          <StyledInputField
            onChange={handleStayCountChange}
            onBlur={handleStayCountBlur}
            type="number"
            style={{ width: 265 }}
            value={stayCountValue}
          />
          <div>방문횟수</div>
        </Row>
        <Row gap={10} justifyContent="flex-start">
          <StyledInputField
            onChange={handleStayNightCountChange}
            onBlur={handleStayNightCountBlur}
            type="number"
            style={{ width: 265 }}
            value={stayNightCountValue}
          />
          <div>숙박일수</div>
        </Row>
      </div>
      {isChanged && (
        <div
          style={{
            fontSize: '12px',
            color: '#d68910',
            fontWeight: 'bold',
          }}
        >
          변경됨
        </div>
      )}
    </div>
  );
};

import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import ServiceHelper from 'stores/ServiceHelper';
import PageLayout from 'components/layouts/PageLayout';
import { InnerGroup, InnerSection } from 'components/layouts/CommonForm';
import { InputField, Row } from 'saladsoft-primitive';
import Button from 'components/input/Button';
import ButtonWrapper from '../listings/ButtonWrapper';
import Text from 'components/commons/Text';

const MemberPolicyPage = observer(() => {
  const location = useLocation();
  const [membershipPolicies, setMembershipPolicies] = useState<AdminMembershipPolicyDto[]>([]);
  const [changedCount, setChangedCount] = useState(0);
  const originalPolicies = useRef<AdminMembershipPolicyDto[]>([]);
  const changedPolicies = useRef<Map<number, AdminMembershipPolicyDto>>(new Map());

  const fetchMembershipPolicies = () => {
    ServiceHelper.adminMembershipPolicyService?.getMembershipPolicies1().then((res) => {
      setMembershipPolicies(res.data);
      originalPolicies.current = res.data;
      changedPolicies.current.clear(); // 새로 가져올 때 변경된 정책 초기화
      setChangedCount(0);
    });
  };

  const handleUpdateMembershipPolicy = (id: number, field: string, value: number) => {
    // 원본 정책 찾기
    const originalPolicy = originalPolicies.current.find((policy) => policy.id === id);

    if (originalPolicy) {
      // 현재 변경된 정책이 있는지 확인
      const existingChangedPolicy = changedPolicies.current.get(id) || { ...originalPolicy };

      // 필드별로 값 업데이트
      const updatedPolicy = {
        ...existingChangedPolicy,
        [field]: value,
      };

      // 원본과 비교해서 전체적으로 변경되었는지 확인
      const isChanged =
        updatedPolicy.pointRate !== originalPolicy.pointRate ||
        updatedPolicy.stayCount !== originalPolicy.stayCount ||
        updatedPolicy.stayNightCount !== originalPolicy.stayNightCount;

      if (isChanged) {
        // 변경된 경우 changedPolicies에 추가/업데이트
        changedPolicies.current.set(id, updatedPolicy);
      } else {
        // 원본과 같은 값으로 되돌아간 경우 changedPolicies에서 제거
        changedPolicies.current.delete(id);
      }

      // UI 업데이트를 위해 현재 상태 재계산
      const updatedPolicies = membershipPolicies.map((policy) => {
        const changedPolicy = changedPolicies.current.get(policy.id as number);
        return changedPolicy || policy;
      });
      setMembershipPolicies(updatedPolicies);
      setChangedCount(changedPolicies.current.size);
    }
  };

  const handleSaveMembershipPolicies = async () => {
    // 변경된 정책들을 배열로 변환
    const changedPoliciesArray = Array.from(changedPolicies.current.values());

    if (changedPoliciesArray.length === 0) {
      console.log('변경된 정책이 없습니다.');
      return;
    }

    console.log('변경된 정책들:', changedPoliciesArray);

    try {
      // 모든 정책 업데이트를 병렬로 실행
      const updatePromises = changedPoliciesArray.map((policy) =>
        ServiceHelper.adminMembershipPolicyService?.updateMembershipPolicy(policy.id as number, {
          level: policy.level as any,
          stayCount: policy.stayCount as number,
          stayNightCount: policy.stayNightCount as number,
          pointRate: policy.pointRate as number,
        }),
      );

      const results = await Promise.all(updatePromises);

      // 모든 요청이 성공했는지 확인
      const allSuccessful = results.every((res) => res?.status === 200);

      if (allSuccessful) {
        // 모든 업데이트가 성공한 경우에만 상태 초기화
        changedPolicies.current.clear();
        setChangedCount(0);
        fetchMembershipPolicies(); // 최신 데이터 다시 가져오기
        console.log('모든 회원 등급 정책이 성공적으로 저장되었습니다.');
      } else {
        console.error('일부 회원 등급 정책 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('회원 등급 정책 저장 중 오류가 발생했습니다:', error);
    }
  };
  useEffect(() => {
    fetchMembershipPolicies();
  }, [location.search]);

  return (
    <PageLayout title="회원 등급정책">
      <InnerSection>
        <InnerGroup>
          <div>
            <Text type="H3">회원 등급 정책</Text>
            <Text type="H4">회원 등급 정책을 설정해주세요.</Text>
            {changedCount > 0 && (
              <div
                style={{
                  marginTop: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#e8f5e8',
                  border: '1px solid #28a745',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#155724',
                }}
              >
                변경된 정책: {changedCount}개
              </div>
            )}
          </div>
          <div>
            {membershipPolicies.map((membershipPolicy) => (
              <MembershipPolicyItem
                key={membershipPolicy.id}
                membershipPolicy={membershipPolicy}
                onUpdate={handleUpdateMembershipPolicy}
                isChanged={changedPolicies.current.has(membershipPolicy.id as number)}
              />
            ))}
          </div>
        </InnerGroup>
      </InnerSection>
      <ButtonWrapper>
        <Button
          onClick={handleSaveMembershipPolicies}
          isValid={changedCount > 0}
          style={{
            opacity: changedCount === 0 ? 0.5 : 1,
            cursor: changedCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          저장 {changedCount > 0 && `(${changedCount}개 변경)`}
        </Button>
      </ButtonWrapper>
    </PageLayout>
  );
});

export default MemberPolicyPage;
